import { useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../services/apiClient';
import PortalModal from '../common/PortalModal/PortalModal';
import ToggleSwitch from '../common/ToggleSwitch/ToggleSwitch';

export default function CreateWorkspaceModal({ isOpen, onClose, onSuccess }) {
  const [createForm, setCreateForm] = useState({
    name: '',
    description: '',
    includePublicContext: false
  });
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [memberSelection, setMemberSelection] = useState('');
  const [existingWorkspaces, setExistingWorkspaces] = useState([]);
  const [linkedWorkspaces, setLinkedWorkspaces] = useState([]);

  useEffect(() => {
    const fetchUsers = async () => {
      if (!isOpen) {
        return;
      }
      try {
        const response = await apiClient.getWorkspaceAllUsers();
        setAvailableUsers(response?.users || []);
      } catch (error) {
        console.error('Error loading workspace users:', error);
        setAvailableUsers([]);
      }
    };

    fetchUsers();
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      const fetchWorkspaces = async () => {
        try {
          const response = await apiClient.getWorkspaces();
          setExistingWorkspaces(response?.workspaces || []);
        } catch (error) {
          console.error('Error fetching workspaces:', error);
          setExistingWorkspaces([]);
        }
      };
      fetchWorkspaces();
    }
  }, [isOpen]);

  const selectableUsers = useMemo(() => {
    let eligibleUsers = availableUsers;

    if (linkedWorkspaces.length > 0) {
      const allowedSets = linkedWorkspaces.map((linkedId) => {
        const ws = existingWorkspaces.find((w) => (w._id || w.id) === linkedId);
        if (!ws) return new Set();

        const membersList = ws.members || [];
        const owner = ws.owner_id || ws.ownerId;
        const wsSet = new Set(membersList);
        if (owner) wsSet.add(owner);
        return wsSet;
      });

      eligibleUsers = eligibleUsers.filter((user) => (
        allowedSets.every((wsSet) => wsSet.has(user.user_sub) || wsSet.has(user.email))
      ));
    }

    const selectedSet = new Set(selectedMembers);
    return eligibleUsers.filter((user) => !selectedSet.has(user.user_sub));
  }, [availableUsers, selectedMembers, linkedWorkspaces, existingWorkspaces]);

  // Auto-remove invalid selected members if inheritance rules change.
  useEffect(() => {
    if (linkedWorkspaces.length > 0) {
      const allowedSets = linkedWorkspaces.map((linkedId) => {
        const ws = existingWorkspaces.find((w) => (w._id || w.id) === linkedId);
        if (!ws) return new Set();

        const wsSet = new Set(ws.members || []);
        const owner = ws.owner_id || ws.ownerId;
        if (owner) wsSet.add(owner);
        return wsSet;
      });

      setSelectedMembers((prev) => prev.filter((memberSub) => {
        const user = availableUsers.find((u) => u.user_sub === memberSub);
        const email = user?.email;
        return allowedSets.every((wsSet) => wsSet.has(memberSub) || (email && wsSet.has(email)));
      }));
    }
  }, [linkedWorkspaces, existingWorkspaces, availableUsers]);

  const handleAddSelectedMember = () => {
    if (!memberSelection) {
      return;
    }
    if (!selectedMembers.includes(memberSelection)) {
      setSelectedMembers((prev) => [...prev, memberSelection]);
    }
    setMemberSelection('');
  };

  const handleRemoveSelectedMember = (memberSub) => {
    setSelectedMembers((prev) => prev.filter((sub) => sub !== memberSub));
  };

  const resetCreateForm = () => {
    setCreateForm({
      name: '',
      description: '',
      includePublicContext: false
    });
    setSelectedMembers([]);
    setMemberSelection('');
    setLinkedWorkspaces([]);
  };

  const handleCloseModal = () => {
    resetCreateForm();
    onClose?.();
  };

  const handleCreateWorkspace = async () => {
    if (!createForm.name.trim()) {
      alert('Workspace name is required.');
      return;
    }

    try {
      await apiClient.createWorkspace({
        name: createForm.name.trim(),
        description: createForm.description.trim(),
        include_public_context: createForm.includePublicContext,
        members: selectedMembers,
        linked_workspaces: linkedWorkspaces
      });
      onSuccess?.();
      handleCloseModal();
    } catch (error) {
      console.error('Error creating workspace:', error);
      alert(`Failed to create workspace: ${error.message}`);
    }
  };

  const createFooter = (
    <>
      <button className="btn btn-secondary" onClick={handleCloseModal}>
        Cancel
      </button>
      <button className="btn btn-primary" onClick={handleCreateWorkspace}>
        Create Workspace
      </button>
    </>
  );

  return (
    <PortalModal
      isOpen={isOpen}
      onClose={handleCloseModal}
      title="Create Workspace"
      size="medium"
      footer={createFooter}
    >
      <div className="modal-body">
        <div className="form-group">
          <label>Name *</label>
          <input
            type="text"
            value={createForm.name}
            onChange={(event) => setCreateForm(prev => ({ ...prev, name: event.target.value }))}
            placeholder="Project Alpha"
          />
        </div>
        <div className="form-group">
          <label>Description</label>
          <textarea
            rows="3"
            value={createForm.description}
            onChange={(event) => setCreateForm(prev => ({ ...prev, description: event.target.value }))}
            placeholder="Describe this workspace"
          />
        </div>
        <div className="form-group" style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid var(--border-subtle)' }}>
          <label style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>Context Inheritance</label>
          <p style={{ fontSize: '0.85em', color: 'var(--text-muted)', marginBottom: '1rem' }}>
            Select other knowledge bases to merge into this workspace. Users invited to this workspace must have access to all selected parent workspaces.
          </p>

          <div className="inheritance-list" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div className="ui-form-group" style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', background: 'var(--bg-surface-hover)', padding: '12px 16px', borderRadius: '8px', border: '1px solid var(--border-subtle)' }}>
              <div>
                <h4 style={{ margin: 0, fontSize: '0.95rem', color: 'var(--text-primary)' }}>Company Context</h4>
                <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-muted)' }}>Include company-wide memories and documents</p>
              </div>
              <ToggleSwitch
                checked={createForm.includePublicContext}
                onChange={(v) => setCreateForm(prev => ({ ...prev, includePublicContext: v }))}
              />
            </div>

            {existingWorkspaces.length > 0 && (
              <div style={{ marginTop: '0.5rem' }}>
                <h5 style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Other Workspaces</h5>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '8px', maxHeight: '200px', overflowY: 'auto' }}>
                  {existingWorkspaces.map(ws => (
                    <div key={ws._id || ws.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', background: 'var(--bg-surface)', borderRadius: '6px', border: '1px solid var(--border-subtle)' }}>
                      <span style={{ fontSize: '0.9rem', fontWeight: '500' }}>{ws.name}</span>
                      <ToggleSwitch
                        checked={linkedWorkspaces.includes(ws._id || ws.id)}
                        onChange={(checked) => {
                          const workspaceId = ws._id || ws.id;
                          if (checked) {
                            setLinkedWorkspaces(prev => [...prev, workspaceId]);
                          } else {
                            setLinkedWorkspaces(prev => prev.filter(id => id !== workspaceId));
                          }
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="form-group">
          <label>Initial Members</label>
          <div style={{ display: 'flex', gap: '8px' }}>
            <select
              className="form-control"
              value={memberSelection}
              onChange={(event) => setMemberSelection(event.target.value)}
            >
              <option value="">-- Select a User --</option>
              {selectableUsers.map((user) => (
                <option key={user.user_sub} value={user.user_sub}>
                  {user.name ? `${user.name} (${user.email})` : user.email || user.user_sub}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleAddSelectedMember}
              disabled={!memberSelection}
            >
              Add
            </button>
          </div>
          {selectedMembers.length > 0 && (
            <div className="source-tags" style={{ marginTop: '8px' }}>
              {selectedMembers.map((memberSub) => {
                const member = availableUsers.find((u) => u.user_sub === memberSub);
                const label = member?.email || member?.name || memberSub;
                return (
                  <span
                    key={memberSub}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '4px 10px',
                      borderRadius: '999px',
                      border: '1px solid var(--border-color)',
                      marginRight: '6px',
                      marginBottom: '6px'
                    }}
                  >
                    {label}
                    <button
                      type="button"
                      onClick={() => handleRemoveSelectedMember(memberSub)}
                      style={{ marginLeft: '6px', border: 'none', background: 'transparent', cursor: 'pointer' }}
                      aria-label={`Remove ${label}`}
                    >
                      ×
                    </button>
                  </span>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </PortalModal>
  );
}
